import { Link } from 'react-router-dom';
import { MapPin, Users, Tent, Calendar, Star } from 'lucide-react';
import { TrustBadge } from './TrustBadge';
import { cn } from '../lib/utils';

export const FacilitatorCard = ({ facilitator }) => {
  return (
    <Link
      to={`/facilitators/${facilitator.id}`}
      className="listing-card group block overflow-hidden"
      data-testid={`facilitator-card-${facilitator.id}`}
    >
      {/* Image */}
      <div className="relative aspect-[4/3] overflow-hidden bg-muted">
        <img
          src={facilitator.profile_photo || 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400'}
          alt={facilitator.full_name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-3 left-3">
          <TrustBadge status={facilitator.verification_status} size="small" />
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="font-heading font-semibold text-lg text-foreground mb-1 group-hover:text-primary transition-colors">
          {facilitator.full_name}
        </h3>
        
        <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
          {facilitator.bio?.replace('[DEMO] ', '')}
        </p>

        {/* Meta info */}
        <div className="flex flex-wrap gap-2 text-xs text-muted-foreground mb-3">
          {facilitator.provinces?.length > 0 && (
            <span className="flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {facilitator.provinces[0]}
              {facilitator.provinces.length > 1 && ` +${facilitator.provinces.length - 1}`}
            </span>
          )}
          {facilitator.years_experience > 0 && (
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {facilitator.years_experience} years
            </span>
          )}
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5">
          {facilitator.camp_types?.slice(0, 3).map((type) => (
            <span
              key={type}
              className="px-2 py-0.5 bg-sage rounded-full text-xs text-white font-medium capitalize"
            >
              {type}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
};

export const CampCard = ({ camp }) => {
  return (
    <Link
      to={`/camps/${camp.id}`}
      className="listing-card group block overflow-hidden"
      data-testid={`camp-card-${camp.id}`}
    >
      {/* Image */}
      <div className="relative aspect-[4/3] overflow-hidden bg-muted">
        <img
          src={camp.images?.[0] || 'https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?w=800'}
          alt={camp.camp_name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-3 left-3">
          <TrustBadge status={camp.verification_status} size="small" />
        </div>
        <div className="absolute bottom-3 right-3 bg-background/90 backdrop-blur-sm px-2 py-1 rounded-lg text-xs font-medium">
          Ages {camp.age_range_min}-{camp.age_range_max}
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <h3 className="font-heading font-semibold text-lg text-foreground mb-1 group-hover:text-primary transition-colors">
          {camp.camp_name}
        </h3>
        
        <div className="flex items-center gap-1 text-sm text-muted-foreground mb-2">
          <MapPin className="w-3.5 h-3.5" />
          {camp.nearest_town}, {camp.province}
        </div>

        <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
          {camp.description?.replace('[DEMO] ', '')}
        </p>

        {/* Meta info */}
        <div className="flex flex-wrap gap-3 text-xs text-muted-foreground mb-3">
          {camp.capacity && (
            <span className="flex items-center gap-1">
              <Users className="w-3 h-3" />
              Capacity: {camp.capacity}
            </span>
          )}
          <span className="flex items-center gap-1 capitalize">
            <Tent className="w-3 h-3" />
            {camp.camp_type}
          </span>
        </div>

        {/* Activities */}
        <div className="flex flex-wrap gap-1.5">
          {camp.activities?.slice(0, 3).map((activity) => (
            <span
              key={activity}
              className="px-2 py-0.5 bg-sage rounded-full text-xs text-white font-medium"
            >
              {activity}
            </span>
          ))}
          {camp.activities?.length > 3 && (
            <span className="px-2 py-0.5 bg-campfire/15 text-campfire-dark rounded-full text-xs font-semibold">
              +{camp.activities.length - 3} more
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};

export default { FacilitatorCard, CampCard };
